/** ч.1098: «Аудит проводок» — перевірка проводок правилами + пояснення Oblik+/AI. */
import { api } from './client';

export type AuditSeverity = 'ERROR' | 'WARN' | 'INFO';
export type FindingStatus = 'OPEN' | 'CHECKED' | 'IGNORED' | 'RESOLVED';
export type ManualStatus = 'OPEN' | 'CHECKED' | 'IGNORED';
export type AuditVerdict = 'likely_error' | 'likely_ok' | 'unclear';

export interface AuditArea {
  code: string;
  title: string;
  rules: { code: string; title: string; severity: AuditSeverity }[];
}

export interface AuditRuleSummary {
  code: string;
  /** область перевірки (ч.1100) */
  area: string;
  area_title: string;
  title: string;
  severity: AuditSeverity;
  why: string;
  action: string;
  open: number;
  checked: number;
  ignored: number;
  resolved: number;
}

export interface AuditLastRun {
  id: string;
  date_from: string;
  date_to: string;
  created_at: string;
  started_by_name: string | null;
  found: number;
  resolved: number;
  rules_failed: number;
  /** правила, де знахідок більше, ніж показано за один запуск */
  truncated: string[];
  /** області, які перевіряв останній запуск */
  areas: string[];
}

export interface AuditSummary {
  rules: AuditRuleSummary[];
  /** остання повна (усі області) перевірка, що покриває запитаний період */
  covering_run: { created_at: string; rules_failed: number } | null;
  open_by_severity: Record<AuditSeverity, number>;
  last_run: AuditLastRun | null;
}

export interface AuditAi {
  verdict: AuditVerdict;
  explanation: string;
  suggestion: string;
  warning: string | null;
  credits: string;
  balance_after: string;
  run: {
    escalated: boolean;
    chosen: string;
    reasons: string[];
    note: string | null;
    attempts: { tier: string; credits: string }[];
  };
  at: string;
}

export interface AuditFinding {
  id: string;
  organization_id: string;
  rule_code: string;
  severity: AuditSeverity;
  status: FindingStatus;
  period_date: string | null;
  document_type: string | null;
  document_id: string | null;
  document_number: string | null;
  document_date: string | null;
  amount: string | null;
  message: string;
  details: Record<string, unknown>;
  note: string | null;
  status_by_name: string | null;
  status_at: string | null;
  ai: AuditAi | null;
  first_seen_at: string;
  last_seen_at: string;
}

export interface AuditRunResult {
  run_id: string;
  found: number;
  resolved: number;
  rules_run: number;
  rules_failed: number;
}

interface Page<T> { items: T[]; total: number; limit: number; offset: number }

/** ч.1102: пошук помилок агентом Oblik+/AI. */
export type ScanStatus = 'RUNNING' | 'PAUSED' | 'DONE' | 'STOPPED';
export type ScanReason = 'manual' | 'rare' | 'big';

export interface ScanParams extends AuditPeriod {
  organization_id: string;
  limit: number;
  include_imported: boolean;
}

export interface ScanPreview {
  documents: number;
  by_reason: Record<ScanReason, number>;
  estimate_credits: string;
  /** скільки кандидатів відсіяно як перенесені з 1С */
  skipped_imported: number;
}

export interface AuditScan {
  id: string;
  organization_id: string;
  date_from: string;
  date_to: string;
  status: ScanStatus;
  params: { limit?: number; include_imported?: boolean; by_reason?: Record<ScanReason, number> };
  total: number;
  done: number;
  issues: number;
  estimate: string;
  charged: string;
  note: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface AuditPeriod { date_from: string; date_to: string }

export const postingAudit = {
  areas: () => api.get<AuditArea[]>('/audit/postings/areas').then((r) => r.data),
  summary: (organization_id: string, period: AuditPeriod, areas?: string[]) =>
    api.get<AuditSummary>('/audit/postings/summary', {
      params: { organization_id, ...period, areas: areas?.length ? areas : undefined },
      paramsSerializer: { indexes: null },   // повторюваний `areas=` для FastAPI list[str]
    }).then((r) => r.data),
  findings: (organization_id: string, period: AuditPeriod, rule_code: string, status?: FindingStatus) =>
    api.get<Page<AuditFinding>>('/audit/postings/findings', {
      params: { organization_id, ...period, rule_code, status, limit: 500 },
    }).then((r) => r.data),
  run: (organization_id: string, period: AuditPeriod, areas?: string[]) =>
    api.post<AuditRunResult>('/audit/postings/run', {
      organization_id, ...period, areas: areas?.length ? areas : null,
    }).then((r) => r.data),
  setStatus: (id: string, status: ManualStatus) =>
    api.patch<AuditFinding>(`/audit/postings/findings/${id}`, { status }).then((r) => r.data),
  setNote: (id: string, note: string) =>
    api.patch<AuditFinding>(`/audit/postings/findings/${id}`, { note }).then((r) => r.data),
  bulkStatus: (organization_id: string, period: AuditPeriod, rule_code: string,
    status: ManualStatus, from_status: ManualStatus) =>
    api.post<{ updated: number }>('/audit/postings/findings/bulk-status', {
      organization_id, rule_code, status, from_status, ...period,
    }).then((r) => r.data),
  explain: (id: string) =>
    api.post<AuditFinding>(`/audit/postings/findings/${id}/explain`).then((r) => r.data),
  scanLatest: (organization_id: string) =>
    api.get<{ scan: AuditScan | null }>('/audit/postings/ai-scan/latest', { params: { organization_id } })
      .then((r) => r.data.scan),
  scanPreview: (p: ScanParams) =>
    api.post<ScanPreview>('/audit/postings/ai-scan/preview', p).then((r) => r.data),
  scanStart: (p: ScanParams) => api.post<AuditScan>('/audit/postings/ai-scan', p).then((r) => r.data),
  scanStep: (id: string) => api.post<AuditScan>(`/audit/postings/ai-scan/${id}/step`).then((r) => r.data),
  scanStop: (id: string) => api.post<AuditScan>(`/audit/postings/ai-scan/${id}/stop`).then((r) => r.data),
};
